import React, { useEffect, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import Navbar from "../../layout/Navbar"
import Review from "../../layout/Review"
import DownloadSection from "../../layout/downloadSection"
import Footer from "../../layout/Footer"
import UpdateCabForm from "./UpdateCabForm"
import MainCab from "./MainCab"
import CustomerFAQ from "./CustomerFAQ"
import SEO from "../../components/SEO"
import ProgressStepper from "../../components/ProgressStepper"
import CityPairPage from "../CityPair"
import { slugify } from "../../utils/slugify"
import { routePath } from "../../utils/urls"
import { titleCase } from "../../utils/routeData"

// Curated real intercity routes, formatted on-brand. Replaces the old three
// duplicated walls of blue links that also pointed at wrong `/..-cab` paths;
// these use the real `/routes/<from>-to-<to>-cab/` URLs.
const POPULAR_ROUTES = [
  ["delhi", "jaipur"], ["delhi", "agra"], ["delhi", "chandigarh"], ["delhi", "dehradun"],
  ["mumbai", "pune"], ["pune", "nashik"], ["ahmedabad", "udaipur"], ["ahmedabad", "rajkot"],
  ["surat", "ahmedabad"], ["vadodara", "ahmedabad"], ["jaipur", "udaipur"], ["jaipur", "jodhpur"],
  ["bangalore", "mysore"], ["chennai", "tirupati"], ["lucknow", "varanasi"], ["chandigarh", "shimla"],
]

const Cab = () => {
  const { location } = useSelector((state) => state.location)
  const { search } = useLocation() // gets the search part of the URL
  const navigate = useNavigate()

  useEffect(() => {
    if (!search) {
      navigate("/")
      return
    }
    // Scroll to top when the pathname changes
    window.scrollTo(0, 0)
  }, [])

  // Keep the booking funnel distraction-free: suppress the Zoho cookie bar on
  // this page (consent is still shown on the landing/home pages).
  useEffect(() => {
    document.documentElement.classList.add("pm-hide-cookiebar")
    return () => document.documentElement.classList.remove("pm-hide-cookiebar")
  }, [])

  // Memoize the queryParams object
  const queryParams = useMemo(() => new URLSearchParams(search), [search])

  const tripData = localStorage.getItem("tripData")

  // Memoize the tripDetails object
  const tripDetails = useMemo(() => {
    const storedData = tripData ? JSON.parse(tripData) : {}

    // Helper to parse number from query param
    const parseNumber = (value) => {
      if (!value || value === "") return ""
      const num = Number(parseFloat(value))
      return isNaN(num) ? "" : num
    }

    // Get values from query params first, then fallback to stored data
    const parsed = {
      ...storedData,
      trip_type: parseInt(queryParams.get("trip_type")) || storedData.trip_type || "",
      round_trip: parseInt(queryParams.get("round_trip")) || storedData.round_trip || 0,
      pickup: queryParams.get("pickup") || storedData.pickup || "",
      drop: queryParams.get("drop") || storedData.drop || "",
      pickup_lat: parseNumber(queryParams.get("pickup_lat")) || storedData.pickup_lat || "",
      pickup_long: parseNumber(queryParams.get("pickup_long")) || storedData.pickup_long || "",
      drop_lat: parseNumber(queryParams.get("drop_lat")) || storedData.drop_lat || "",
      drop_long: parseNumber(queryParams.get("drop_long")) || storedData.drop_long || "",
      pickup_city: queryParams.get("pickup_city") || storedData.pickup_city || "",
      drop_city: queryParams.get("drop_city") || storedData.drop_city || "",
      trip_date: queryParams.get("trip_date") || storedData.trip_date || "",
      trip_time: queryParams.get("trip_time") || storedData.trip_time || "",
      phone_number: queryParams.get("phone_number") || storedData.phone_number || "",
      return_date: queryParams.get("return_date") || storedData.return_date || "",
      return_time: queryParams.get("return_time") || storedData.return_time || "",
      package_type_in_hours: queryParams.get("package_type_in_hours") || storedData.package_type_in_hours || "",
      ip_address: location?.ip || storedData.ip_address || 8425,
    }

    return parsed
  }, [queryParams, location, tripData])

  useEffect(() => {
    localStorage.setItem("tripData", JSON.stringify(tripDetails))
  }, [tripDetails])

  // Check if this is an outstation one-way trip with city names
  const shouldShowCityPairPage = useMemo(() => {
    const tripType = parseInt(queryParams.get("trip_type"))
    const roundTrip = parseInt(queryParams.get("round_trip"))
    const pickupCity = queryParams.get("pickup_city")
    const dropCity = queryParams.get("drop_city")

    // Show CityPair page if: trip_type=3 (outstation), round_trip=0 or 1, and both cities are present
    return tripType === 3 && (roundTrip === 0 || roundTrip === 1) && pickupCity && dropCity
  }, [queryParams])

  // Extract city slugs for CityPair page
  const citySlugs = useMemo(() => {
    if (!shouldShowCityPairPage) return null

    const pickupCity = queryParams.get("pickup_city")
    const dropCity = queryParams.get("drop_city")

    return {
      fromCity: slugify(pickupCity),
      toCity: slugify(dropCity),
    }
  }, [shouldShowCityPairPage, queryParams])

  // If conditions are met, render CityPairPage
  if (shouldShowCityPairPage && citySlugs) {
    return <CityPairPage fromCity={citySlugs.fromCity} toCity={citySlugs.toCity} />
  }

  return (
    <div>
      <SEO
        title="Book Cab"
        description="Book your cab with Pulpit - Choose from local, outstation, or hourly rental services. Find the perfect vehicle for your journey with transparent pricing and verified drivers."
        keywords="book cab, taxi booking, cab service, outstation cab, local cab, hourly rental, Pulpit cab booking"
        noindex
      />
      <Navbar page="cabs" />
      <ProgressStepper currentStep={2} />
      <UpdateCabForm />
      <MainCab tripDetails={tripDetails} />

      {/* Popular routes — one clean, on-brand section */}
      <section className="max-w-[1180px] mx-auto px-4 md:px-8 py-12">
        <div className="bg-white rounded-2xl border border-[#eef2f1] shadow-[0_4px_22px_rgba(16,40,37,0.05)] px-5 py-7 md:px-10 md:py-9">
          <h2 className="text-[18px] md:text-[22px] font-black text-[#16211f] mb-1">Popular cab routes</h2>
          <p className="text-[13px] md:text-[14px] text-[#6b7280] mb-6">
            Fixed, all-inclusive fares on India&apos;s most-booked intercity routes.
          </p>
          <div className="flex flex-wrap gap-2.5">
            {POPULAR_ROUTES.map(([from, to]) => (
              <a
                key={`${from}-${to}`}
                href={routePath(from, to)}
                className="inline-flex items-center gap-2 rounded-full border border-[#e0ebe9] bg-[#f7fbfa] px-4 py-2 text-[13px] font-bold text-[#16211f] hover:bg-[#21A5A0] hover:border-[#21A5A0] hover:text-white transition-colors no-underline"
              >
                {titleCase(from)} <span className="opacity-50">&rarr;</span> {titleCase(to)}
              </a>
            ))}
          </div>
        </div>
      </section>

      <CustomerFAQ> </CustomerFAQ>
      <Review />
      <DownloadSection />
      <Footer />
    </div>
  )
}

export default Cab
